import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Space,
  Popconfirm,
  message,
  Card,
  Typography,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Role {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  level: number;
  permissions: Record<string, any>;
  is_active: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

const RoleManagement: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch roles
  const { data: rolesData, isLoading } = useQuery(
    'roles',
    () => api.get('/api/roles').then(res => res.data),
    {
      onError: (error: any) => {
        message.error('Failed to load roles: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  // Create role mutation
  const createMutation = useMutation(
    (roleData: Partial<Role>) => api.post('/api/roles', roleData),
    {
      onSuccess: () => {
        message.success('Role created successfully');
        queryClient.invalidateQueries('roles');
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error('Failed to create role: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  // Update role mutation
  const updateMutation = useMutation(
    ({ id, ...roleData }: Partial<Role> & { id: string }) => 
      api.put(`/api/roles/${id}`, roleData),
    {
      onSuccess: () => {
        message.success('Role updated successfully');
        queryClient.invalidateQueries('roles');
        setIsModalVisible(false);
        setEditingRole(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error('Failed to update role: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  // Delete role mutation
  const deleteMutation = useMutation(
    (id: string) => api.delete(`/api/roles/${id}`),
    {
      onSuccess: () => {
        message.success('Role deleted successfully');
        queryClient.invalidateQueries('roles');
      },
      onError: (error: any) => {
        message.error('Failed to delete role: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  const handleCreate = () => {
    setEditingRole(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (role: Role) => {
    setEditingRole(role);
    setIsModalVisible(true);
    form.setFieldsValue({
      ...role,
      permissions: JSON.stringify(role.permissions, null, 2)
    });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = (values: any) => {
    const roleData = {
      ...values,
      permissions: values.permissions ? JSON.parse(values.permissions) : {}
    };

    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, ...roleData });
    } else {
      createMutation.mutate(roleData);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Role) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary">{record.display_name}</Text>
        </Space>
      )
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      render: (level: number) => (
        <Tag color={level >= 50 ? 'red' : level >= 20 ? 'orange' : level >= 10 ? 'blue' : 'green'}>
          Level {level}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean, record: Role) => (
        <Space>
          <Tag color={isActive ? 'green' : 'red'}>
            {isActive ? 'Active' : 'Inactive'}
          </Tag>
          {record.is_system && (
            <Tag color="purple">System</Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Role) => (
        <Space>
          <Tooltip title="Edit Role">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={record.is_system}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this role?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.is_system}
          >
            <Tooltip title={record.is_system ? "System roles cannot be deleted" : "Delete Role"}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={record.is_system}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Role Management</Title>
          <Text type="secondary">
            Create and manage user roles with custom permissions and hierarchy levels
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Create Role
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={rolesData?.roles || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} roles`
        }}
      />

      <Modal
        title={editingRole ? 'Edit Role' : 'Create Role'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingRole(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Role Name"
            rules={[
              { required: true, message: 'Please enter role name' },
              { pattern: /^[a-z_]+$/, message: 'Role name must contain only lowercase letters and underscores' }
            ]}
            tooltip="Used internally (e.g., 'support_manager')"
          >
            <Input placeholder="e.g., support_manager" />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="Display Name"
            rules={[{ required: true, message: 'Please enter display name' }]}
            tooltip="Shown to users (e.g., 'Support Manager')"
          >
            <Input placeholder="e.g., Support Manager" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            tooltip="Optional description of the role's responsibilities"
          >
            <TextArea rows={3} placeholder="Describe the role's responsibilities..." />
          </Form.Item>

          <Form.Item
            name="level"
            label="Hierarchy Level"
            rules={[{ required: true, message: 'Please enter hierarchy level' }]}
            tooltip="Higher levels have more privileges (0-1000)"
          >
            <InputNumber
              min={0}
              max={1000}
              style={{ width: '100%' }}
              placeholder="Enter level (0-1000)"
            />
          </Form.Item>

          <Form.Item
            name="permissions"
            label="Permissions"
            tooltip="JSON object defining role-specific permissions"
          >
            <TextArea
              rows={6}
              placeholder='{"can_escalate": true, "can_delete_tickets": false, "can_manage_users": true}'
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isLoading || updateMutation.isLoading}
              >
                {editingRole ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default RoleManagement;
